/**
 * `BeatField` (W1-12 ui-kit-i18n; contract BeatFieldProps, DESIGN.md §4.11, WORKPACKAGES GRID-03).
 *
 *  - Accepts every beat syntax of core/time `parseBeat`: `12:3/4`, `12 3/4`, `12+3/4`, `49/4`, decimals,
 *    `12;1` and RPE `12.1` (by `ctx.syntax`), seconds `23.5s` / `@1:23.5`, markers `m12+2`, and relative input
 *    `+1/4` / `-2`.
 *  - Live echo under the field while editing ("= 12:1/4 · 0:23.512", plus the rounding of seconds input), and
 *    the parse error with its position otherwise. With `echo`, the committed value's time is shown when idle.
 *  - Enter / blur commit when valid (Enter keeps a wrong text for fixing, blur restores the value); Escape
 *    restores. ArrowUp / ArrowDown step by one snap division (`ctx.snapDiv`), PageUp / PageDown by one beat; the
 *    stepped value is committed when the key is released.
 *  - `onCommit(beat, relative)`: for a single value, relative input is resolved against it and arrives as an
 *    absolute beat (`relative` false). For a mixed value, `+1/4` arrives as the delta with `relative` true (the
 *    caller moves every item by it); absolute input sets every item.
 */

import { useId, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Beats } from '../core/time/beat';
import { formatBeat, formatSec, parseBeat } from '../core/time/beatSyntax';
import type { Beat, BeatParseContext, BeatParseResult } from '../core/time/types';
import { useT } from '../i18n/useT';
import s from './BeatField.module.css';
import { useTr } from './bridge';
import { Icon } from './Icon';
import { cx } from './internal/cx';
import { pendingEditProps } from './internal/layers';
import f from './internal/form.module.css';
import type { BeatFieldProps } from './types';
import { m } from './ui.messages';

/** The parse context for this field's value: single values are the base of relative input, mixed ones are not. */
export function beatContextFor(ctx: BeatParseContext, value: BeatFieldProps['value']): BeatParseContext {
  const base: BeatParseContext = {
    snapDiv: ctx.snapDiv,
    syntax: ctx.syntax,
    ...(ctx.tempo === undefined ? {} : { tempo: ctx.tempo }),
    ...(ctx.marker === undefined ? {} : { marker: ctx.marker }),
  };
  return value.kind === 'value' ? { ...base, current: value.value } : base;
}

/** "= 0:24.512" for an idle value (null without a tempo map). */
function idleEcho(beat: Beat, ctx: BeatParseContext): string | null {
  return ctx.tempo === undefined ? null : `= ${formatSec(ctx.tempo.secAt(beat))}`;
}

export interface BeatFieldExtraProps {
  readonly className?: string | undefined;
  readonly stacked?: boolean | undefined;
  readonly autoFocus?: boolean | undefined;
}

export function BeatField(props: BeatFieldProps & BeatFieldExtraProps) {
  const t = useT();
  const tr = useTr();
  const inputId = useId();
  const echoId = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const [stepped, setStepped] = useState<Beat | null>(null);
  const [focused, setFocused] = useState(false);
  const disabled = props.disabled === true;
  const ctx = useMemo(() => beatContextFor(props.ctx, props.value), [props.ctx, props.value]);
  const current = props.value.kind === 'value' ? props.value.value : null;
  const shownBeat = stepped ?? current;
  const text = draft ?? (shownBeat === null ? '' : formatBeat(shownBeat));
  const parsed: BeatParseResult | null = useMemo(() => (draft === null ? null : parseBeat(draft, ctx)), [draft, ctx]);

  const commitBeat = (beat: Beat, relative: boolean): void => {
    if (!relative && current !== null && Beats.eq(beat, current)) return;
    props.onCommit(beat, relative);
  };

  const commitDraft = (fromBlur: boolean): void => {
    if (draft === null) return;
    if (draft.trim() === '') {
      setDraft(null);
      return;
    }
    const r = parseBeat(draft, ctx);
    if (!r.ok) {
      if (fromBlur) setDraft(null);
      return;
    }
    setDraft(null);
    commitBeat(r.beat, r.relative && ctx.current === undefined);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    const arrow = e.key === 'ArrowUp' || e.key === 'ArrowDown';
    const page = e.key === 'PageUp' || e.key === 'PageDown';
    if ((arrow || page) && !disabled && !e.altKey && !e.ctrlKey && !e.metaKey) {
      let base = stepped ?? current;
      if (draft !== null) {
        const r = parseBeat(draft, ctx);
        if (!r.ok || (r.relative && ctx.current === undefined)) return;
        base = r.beat;
      }
      if (base === null) return;
      e.preventDefault();
      const dir = e.key === 'ArrowUp' || e.key === 'PageUp' ? 1 : -1;
      const next = page ? Beats.step(base, 1, dir) : Beats.step(base, Math.max(1, Math.round(ctx.snapDiv)), dir);
      setDraft(null);
      setStepped(next);
      return;
    }
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      commitDraft(false);
      return;
    }
    if (e.key === 'Escape' && (draft !== null || stepped !== null)) {
      e.preventDefault();
      e.stopPropagation();
      setDraft(null);
      setStepped(null);
    }
  };
  const onKeyUp = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (stepped === null) return;
    if (!['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'].includes(e.key)) return;
    const beat = stepped;
    setStepped(null);
    commitBeat(beat, false);
  };

  let echo: { readonly kind: 'ok' | 'error' | 'hint'; readonly text: string } | null = null;
  if (parsed !== null && draft !== null && draft.trim() !== '') {
    if (parsed.ok) {
      const rounded =
        parsed.roundingMs !== undefined && Math.abs(parsed.roundingMs) >= 0.0005
          ? t(m['beat.rounded'], { echo: parsed.echo, ms: parsed.roundingMs })
          : parsed.echo;
      const delta = parsed.relative && ctx.current === undefined;
      echo = {
        kind: 'ok',
        text: delta ? t(m['beat.relative'], { delta: parsed.echo.replace(/^=\s*/, '') }) : rounded,
      };
    } else {
      echo = { kind: 'error', text: tr(parsed.error) };
    }
  } else if (focused && draft !== null) {
    echo = { kind: 'hint', text: t(m['beat.hint']) };
  } else if (props.echo === true && shownBeat !== null) {
    const idle = idleEcho(shownBeat, ctx);
    if (idle !== null) echo = { kind: 'ok', text: idle };
  }

  return (
    <div className={cx(s.field, props.stacked === true && s.stacked, props.className)} data-help-id={props.helpId}>
      <label htmlFor={inputId} className={cx(f.label, s.label)}>
        <span className={f.labelText}>{props.label}</span>
      </label>
      <div className={s.controlCell}>
        <input
          id={inputId}
          className={cx(f.input, s.input)}
          type="text"
          autoComplete="off"
          spellCheck={false}
          value={text}
          placeholder={current === null ? t(m.mixed) : undefined}
          disabled={disabled}
          autoFocus={props.autoFocus}
          aria-invalid={parsed !== null && !parsed.ok ? true : undefined}
          aria-describedby={echo === null ? undefined : echoId}
          onChange={(e) => {
            setStepped(null);
            setDraft(e.target.value);
          }}
          onFocus={(e) => {
            setFocused(true);
            e.currentTarget.select();
          }}
          onBlur={() => {
            setFocused(false);
            if (stepped !== null) {
              const beat = stepped;
              setStepped(null);
              commitBeat(beat, false);
            }
            commitDraft(true);
          }}
          {...pendingEditProps(draft !== null || stepped !== null)}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
        />
        {echo !== null && (
          <p id={echoId} className={echo.kind === 'error' ? f.error : echo.kind === 'hint' ? f.help : f.message}>
            {echo.kind === 'error' && <Icon name="CircleAlert" size={12} />}
            <span>{echo.text}</span>
          </p>
        )}
      </div>
    </div>
  );
}
